import type { LucideIcon } from 'lucide-react';
import { CloudUpload, Contact, FileText, Info, LogOut, Plug, ScrollText, Shield, User } from 'lucide-react';

export const DRAWER_WIDTH = 240;

export type NavSection = 'workspace' | 'resources' | 'account';

export type NavItemType = 'app' | 'marketing';

export interface NavItem {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  type: NavItemType;
  path?: string;
  marketingPath?: string;
  section: NavSection;
  requiresAuth?: boolean;
}

export interface ProfileMenuItem {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  type: 'app' | 'marketing' | 'action';
  path?: string;
  marketingPath?: string;
  action?: 'logout';
  showInAppShell?: boolean;
  showInFocusShell?: boolean;
}

export const NAV_SECTIONS: { id: NavSection; labelKey: string }[] = [
  { id: 'workspace', labelKey: 'navWorkspace' },
  { id: 'resources', labelKey: 'navResources' },
  { id: 'account', labelKey: 'navAccount' },
];

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'documents',
    labelKey: 'documentList',
    icon: FileText,
    type: 'app',
    path: '/documents',
    section: 'workspace',
    requiresAuth: true,
  },
  {
    id: 'upload',
    labelKey: 'uploadDocument',
    icon: CloudUpload,
    type: 'app',
    path: '/upload',
    section: 'workspace',
    requiresAuth: true,
  },
  {
    id: 'integration',
    labelKey: 'integration',
    icon: Plug,
    type: 'app',
    path: '/integration',
    section: 'resources',
    requiresAuth: true,
  },
  {
    id: 'about',
    labelKey: 'aboutUs',
    icon: Info,
    type: 'marketing',
    marketingPath: '/about',
    section: 'resources',
    requiresAuth: true,
  },
  {
    id: 'contact',
    labelKey: 'contactUs',
    icon: Contact,
    type: 'marketing',
    marketingPath: '/contact',
    section: 'resources',
    requiresAuth: true,
  },
  {
    id: 'privacy',
    labelKey: 'privacyPolicy',
    icon: Shield,
    type: 'marketing',
    marketingPath: '/about#privacy',
    section: 'resources',
    requiresAuth: true,
  },
  {
    id: 'terms',
    labelKey: 'termsOfService',
    icon: ScrollText,
    type: 'marketing',
    marketingPath: '/about#terms',
    section: 'resources',
    requiresAuth: true,
  },
  {
    id: 'profile',
    labelKey: 'profile',
    icon: User,
    type: 'app',
    path: '/profile',
    section: 'account',
    requiresAuth: true,
  },
];

export const PROFILE_MENU_ITEMS: ProfileMenuItem[] = [
  {
    id: 'profile',
    labelKey: 'profile',
    icon: User,
    type: 'app',
    path: '/profile',
    showInAppShell: false,
    showInFocusShell: true,
  },
  {
    id: 'privacy',
    labelKey: 'privacyPolicy',
    icon: Shield,
    type: 'marketing',
    marketingPath: '/about#privacy',
    showInAppShell: false,
    showInFocusShell: true,
  },
  {
    id: 'terms',
    labelKey: 'termsOfService',
    icon: ScrollText,
    type: 'marketing',
    marketingPath: '/about#terms',
    showInAppShell: false,
    showInFocusShell: true,
  },
  {
    id: 'logout',
    labelKey: 'logout',
    icon: LogOut,
    type: 'action',
    action: 'logout',
    showInAppShell: true,
    showInFocusShell: true,
  },
];

export const PAGE_TITLE_KEYS: Record<string, string> = {
  '/documents': 'documentList',
  '/upload': 'uploadDocument',
  '/profile': 'profile',
  '/integration': 'integration',
  '/api-reference': 'apiReference',
  '/document': 'document',
};

export function getVisibleNavItems(isAuthenticated: boolean): NavItem[] {
  return NAV_ITEMS.filter((item) => !item.requiresAuth || isAuthenticated);
}

export function getNavItemsBySection(isAuthenticated: boolean): Map<NavSection, NavItem[]> {
  const grouped = new Map<NavSection, NavItem[]>();
  for (const section of NAV_SECTIONS) {
    grouped.set(section.id, []);
  }
  for (const item of getVisibleNavItems(isAuthenticated)) {
    grouped.get(item.section)?.push(item);
  }
  return grouped;
}

export function getProfileMenuItems(shell: 'app' | 'focus'): ProfileMenuItem[] {
  return PROFILE_MENU_ITEMS.filter((item) =>
    shell === 'app' ? item.showInAppShell : item.showInFocusShell,
  );
}
